import { useState } from 'react';
import { ClipboardList, Copy, Check, Eye } from 'lucide-react';
import { useToastStore } from '../../store/toastStore';

interface ChecklistItem {
  day: string;
  task: string;
  goal: string;
}

interface Props {
  startup: Record<string, any>;
  validationPlanData: Record<string, any> | null;
  onOpenChecklist: () => void;
  onLogResults: () => void;
  onViewBlueprint: () => void;
}

const C = {
  bg: '#0d0d1f',
  surface: '#161627',
  border: '#2a2a3e',
  primary: '#7c6ff7',
  accent: '#00d4ff',
  green: '#22c55e',
  amber: '#f59e0b',
  text: '#f0f0ff',
  text2: '#8888aa',
  text3: '#444466',
};

function parseDayNumber(dayStr: string): number {
  const match = dayStr.match(/(\d+)/);
  return match ? parseInt(match[1]) : 0;
}

export default function DailyMissionCard({ startup, validationPlanData, onOpenChecklist, onLogResults, onViewBlueprint }: Props) {
  const [dmCopied, setDmCopied] = useState(false);
  const { addToast } = useToastStore();
  const startDate = startup.validationStartDate ? new Date(startup.validationStartDate) : null;
  const rawDay = startDate ? Math.floor((Date.now() - startDate.getTime()) / 86400000) + 1 : 1;
  const currentDay = Math.max(1, Math.min(rawDay, 14));
  const progressPct = Math.round((currentDay / 14) * 100);

  const hasResults = !!startup.validationResults?.loggedAt || !!startup.validationResults?.submittedAt;
  const hasMvp = startup.mvpBlueprint?.status === 'complete' && (startup.mvpBlueprint?.data || startup.mvpBlueprint?.buildPlan);
  const isOver = rawDay > 14 || hasResults;

  const checklist: ChecklistItem[] = (validationPlanData?.checklist as ChecklistItem[]) ?? [];

  // Find today's tasks
  const todayTasks = checklist.filter(item => {
    const dayNum = parseDayNumber(item.day);
    return dayNum === currentDay || (item.day && item.day.includes(`${currentDay}`));
  });
  // Fallback: show a task from the nearest day
  const todayTask = todayTasks[0] ?? checklist[Math.min(currentDay - 1, checklist.length - 1)] ?? null;

  // Copy DM template if available
  const dmMessage = validationPlanData?.dmMessage as string;

  // Completion state
  if (hasMvp) {
    return (
      <div style={{
        background: C.surface, borderRadius: 14,
        borderLeft: `4px solid ${C.green}`,
        padding: '20px 24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 700, color: C.green, marginBottom: 4 }}>MVP Blueprint unlocked</p>
            <p style={{ fontSize: 12, color: C.text2 }}>Your personalized build plan is ready.</p>
          </div>
          <button onClick={onViewBlueprint} style={{
            background: `${C.green}15`, border: `1px solid ${C.green}40`,
            borderRadius: 10, color: C.green, fontSize: 13, fontWeight: 600,
            padding: '8px 18px', cursor: 'pointer',
          }}>
            View Blueprint ↓
          </button>
        </div>
      </div>
    );
  }

  if (isOver) {
    return (
      <div style={{
        background: C.surface, borderRadius: 14,
        borderLeft: `4px solid ${C.amber}`,
        padding: '20px 24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>
              Your 14-day experiment is complete
            </p>
            <p style={{ fontSize: 12, color: C.text2 }}>
              Time to log your results and unlock your MVP Blueprint.
            </p>
          </div>
          <button onClick={onLogResults} style={{
            background: `linear-gradient(135deg, ${C.amber}, #ff8c00)`,
            border: 'none', borderRadius: 10, color: '#fff',
            fontSize: 13, fontWeight: 700, padding: '10px 20px', cursor: 'pointer',
          }}>
            Log My Results →
          </button>
        </div>
      </div>
    );
  }

  // Active validation state
  return (
    <div style={{
      background: C.surface, borderRadius: 14,
      borderLeft: `4px solid ${C.accent}`,
      padding: '20px 24px',
    }}>
      {/* Day counter + progress */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.accent }}>Day {currentDay} of 14</span>
        <span style={{ fontSize: 11, color: C.text3 }}>{progressPct}%</span>
      </div>
      <div style={{ height: 4, background: C.bg, borderRadius: 2, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{
          height: '100%', width: `${progressPct}%`,
          background: `linear-gradient(90deg, ${C.accent}, ${C.green})`,
          borderRadius: 2, transition: 'width 0.5s ease',
        }} />
      </div>

      {/* Today's mission */}
      {todayTask && (
        <div style={{ marginBottom: 14 }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: C.text3, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
            Today's Mission
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ color: C.accent, fontSize: 14 }}>▶</span>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{todayTask.task}</p>
              {todayTask.goal && <p style={{ fontSize: 12, color: C.text2, marginTop: 2 }}>{todayTask.goal}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={onOpenChecklist} style={{
          background: `${C.primary}12`, border: `1px solid ${C.primary}30`,
          borderRadius: 8, color: C.primary, fontSize: 12, fontWeight: 600,
          padding: '7px 14px', cursor: 'pointer',
        }}>
          <ClipboardList size={14} /> Open Checklist
        </button>
        {dmMessage && (
          <button onClick={() => {
            navigator.clipboard.writeText(dmMessage)
              .then(() => {
                addToast('DM copied to clipboard!', 'success');
                setDmCopied(true);
                setTimeout(() => setDmCopied(false), 1500);
              })
              .catch(() => {
                addToast('Could not copy — please copy manually.', 'error');
              });
          }} style={{
            background: dmCopied ? `${C.green}12` : `${C.accent}12`,
            border: `1px solid ${dmCopied ? `${C.green}30` : `${C.accent}30`}`,
            borderRadius: 8, color: dmCopied ? C.green : C.accent, fontSize: 12, fontWeight: 600,
            padding: '7px 14px', cursor: 'pointer', transition: 'all 0.2s',
          }}>
            {dmCopied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Message</>}
          </button>
        )}
      </div>
    </div>
  );
}
